import axios from 'axios';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { roleMapping } from '../constants';
import { UserData } from '../interfaces/user';
import { PermissionString } from '../utils/permissions';
import { useAuth } from './AuthContext';

interface UserContextProps {
  userData: UserData | null;
  isLoading: boolean;
  refreshUser: () => void;
  hasPermission: (permission: PermissionString) => boolean;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { permissions, hasPermission, isLoading: authLoading } = useAuth();

  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get('/api/users/whoami');

      if (response.status !== 200) {
        throw new Error('Failed to fetch user data');
      }

      const data = response.data;

      // DEPRECATED: role or custom:Role is not to be referenced in new pages
      data.role = Number.parseInt(data['role']);
      data.role_name = roleMapping[data.role] || 'User';

      // Get permissions from AuthContext
      data.permissions = permissions;

      setUserData(data);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [permissions]);

  useEffect(() => {
    // Wait for AuthContext to finish loading permissions before fetching user data.
    // This prevents a race condition where refreshUser captures stale permissions
    // in its closure, which manifests as permission checks failing in Safari.
    if (!authLoading) {
      refreshUser();
    }
  }, [authLoading, refreshUser]);

  const userContextValue = useMemo(
    () => ({
      userData,
      refreshUser,
      isLoading,
      hasPermission,
    }),
    [userData, refreshUser, isLoading, hasPermission]
  );

  return (
    <UserContext.Provider value={userContextValue}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
